import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import type { AuthResult } from '@/core/user/auth.model';
import { AuthSuccess, NewPasswordRequiredChallenge } from '@/core/user/auth.model';
import { LOGIN_URL, SAML_TOKEN_URL } from './auth.config';

export type LoginErrorCode = 'UNEXPECTED';

export class LoginError extends Error {
  constructor(readonly code: LoginErrorCode) {
    super(code);
  }
}

interface CognitoAuthenticationResult {
  idToken?: string;
  accessToken?: string;
  refreshToken?: string;
  expiresIn?: number;
}

interface CognitoAuthenticationResponse {
  session?: string;
  authenticationResult?: CognitoAuthenticationResult;
  challengeName?: string;
}

@Injectable({ providedIn: 'root' })
export class AuthApiService {
  private http = inject(HttpClient);

  async login(email: string, password: string): Promise<AuthResult> {
    try {
      const response = await firstValueFrom(
        this.http.post<CognitoAuthenticationResponse>(LOGIN_URL, { email, password }),
      );
      return mapAuthResponse(response);
    } catch (error) {
      throw new LoginError(mapLoginErrorCode(error));
    }
  }

  async refresh(refreshToken: string): Promise<AuthSuccess> {
    try {
      const response = await firstValueFrom(this.http.post<CognitoAuthenticationResponse>(LOGIN_URL, { refreshToken }));
      return AuthSuccess.create(response.authenticationResult ?? {});
    } catch (error) {
      throw new LoginError(mapLoginErrorCode(error));
    }
  }

  async loginWithSamlCode(code: string): Promise<AuthSuccess> {
    try {
      const response = await firstValueFrom(
        this.http.post<CognitoAuthenticationResponse>(SAML_TOKEN_URL, {}, { params: { code } }),
      );
      return AuthSuccess.create(response.authenticationResult ?? {});
    } catch (error) {
      throw new LoginError(mapLoginErrorCode(error));
    }
  }
}

function mapLoginErrorCode(error: unknown): LoginErrorCode {
  if (error instanceof LoginError) return error.code;
  return 'UNEXPECTED';
}

function mapAuthResponse(response: CognitoAuthenticationResponse): AuthResult {
  const { challengeName, session } = response;
  if (challengeName !== undefined) {
    if (challengeName !== 'NEW_PASSWORD_REQUIRED') throw new Error(`Unsupported challenge: ${challengeName}`);
    return NewPasswordRequiredChallenge.create(session);
  }

  return AuthSuccess.create(response.authenticationResult ?? {});
}
